import { BANK_ACCOUNT_KIND_LABELS, type BankAccount } from "@station/contracts";
import {
  Alert,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { BankReconciliationTab } from "../../../../features/treasury/bank-reconciliation.js";
import { TreasuryPage, useFinanceCrumb } from "../../../../features/treasury/shared.js";
import { TransactionsList } from "../../../../features/treasury/transactions-list.js";
import { formatIban } from "../../../../features/treasury/treasury-values.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

const TABS = ["transactions", "reconciliation"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/finans/bankalar/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: BankAccountDetailPage,
});

function BankAccountDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const listCrumb = useFinanceCrumb("/finans/bankalar", "menu.bankAccounts");
  const account = useQuery({
    queryKey: queryKeys.treasury(null, "bank-account", id),
    queryFn: async () => (await api.GET("/bank-accounts/{id}", { params: { path: { id } } })).data!,
  });

  return (
    <TreasuryPage
      title={account.data ? `${account.data.bankName} · ${account.data.name}` : t("menu.bankAccounts")}
      breadcrumb={[listCrumb, { label: account.data?.name ?? "…" }]}
    >
      {({ companyId }) =>
        account.error ? (
          <Alert tone="danger">{errorMessage(account.error)}</Alert>
        ) : !account.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <AccountTabs companyId={companyId} account={account.data} />
        )
      }
    </TreasuryPage>
  );
}

function AccountTabs({ companyId, account }: { companyId: string; account: BankAccount }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab: TabKey = Route.useSearch().tab ?? "transactions";

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.balance")}</span>
          <MoneyText value={account.balance} colorize className="text-lg font-semibold" />
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.availableBalance")}</span>
          <MoneyText value={account.availableBalance} colorize className="font-semibold" />
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.kind")}</span>
          <span className="font-medium">{BANK_ACCOUNT_KIND_LABELS[account.kind]}</span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.iban")}</span>
          <span className="font-mono text-xs font-medium">{formatIban(account.iban)}</span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.ledgerAccount")}</span>
          <span className="tabular font-medium">{account.ledgerAccountCode ?? "—"}</span>
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-muted-foreground">{t("treasury.fields.lastStatement")}</span>
          <span className="tabular font-medium">{formatDate(account.lastStatementDate)}</span>
        </span>
        {account.isActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
      </div>

      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            search: (previous) => ({ company: previous.company, tab: next as TabKey }),
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("treasury.bankAccounts.tabsLabel")} className="flex-wrap">
          {TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`treasury.bankAccounts.tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="transactions">
          {tab === "transactions" ? (
            <TransactionsList
              companyId={companyId}
              filter={{ bankAccountId: account.id }}
              caption={t("treasury.bankAccounts.tabs.transactions")}
            />
          ) : null}
        </TabsContent>
        <TabsContent value="reconciliation">
          {tab === "reconciliation" ? (
            <BankReconciliationTab companyId={companyId} bankAccountId={account.id} />
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
